// Small CSV reader for bulk-email recipient uploads. Handles quotes, escaped quotes, CRLF,
// a UTF-8 BOM and comma / semicolon / tab delimiters (Excel in some regions saves with ';').

export const EMAIL_RE = /^[^@\s,;<>]+@[^@\s,;<>]+\.[^@\s,;<>]+$/;

function detectDelimiter(line) {
  const counts = { ",": 0, ";": 0, "\t": 0 };
  let inQ = false;
  for (const ch of line) {
    if (ch === '"') inQ = !inQ;
    else if (!inQ && ch in counts) counts[ch] += 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : ",";
}

// Returns [{ line, cells }] (1-based physical line of each record), skipping blank lines.
export function parseCsv(text) {
  const src = (text || "").replace(/^﻿/, "");
  const delim = detectDelimiter(src.split(/\r\n|\n|\r/, 1)[0] || "");
  const rows = [];
  let cells = [], cell = "", inQ = false, line = 1, startLine = 1;
  const endRow = () => {
    cells.push(cell);
    if (cells.some((c) => c.trim() !== "")) rows.push({ line: startLine, cells });
    cells = []; cell = "";
  };
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQ) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i += 1; } else inQ = false;
      } else { if (ch === "\n") line += 1; cell += ch; }
    } else if (ch === '"') inQ = true;
    else if (ch === delim) { cells.push(cell); cell = ""; }
    else if (ch === "\r" || ch === "\n") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      endRow(); line += 1; startLine = line;
    } else cell += ch;
  }
  if (cell !== "" || cells.length) endRow();
  return rows;
}

const HEADERS = {
  email: ["email", "email address", "e-mail", "emailaddress", "email_address"],
  first: ["first_name", "first name", "firstname", "first", "given name", "preferred name", "preferred_name"],
  last: ["last_name", "last name", "lastname", "last", "surname", "family name"],
};
const colOf = (headers, names) => headers.findIndex((h) => names.includes(h));

// -> { rows: [{ email, first_name, name, kind: "manual" }], problems: [{ line, email, reason }], noName }
export function parseRecipientsCsv(text) {
  const records = parseCsv(text);
  const out = { rows: [], problems: [], noName: 0 };
  if (!records.length) return out;
  const head = records[0].cells.map((c) => c.trim().toLowerCase());
  let ei = colOf(head, HEADERS.email), fi = colOf(head, HEADERS.first), li = colOf(head, HEADERS.last);
  let data = records.slice(1);
  if (ei === -1) {
    // No recognisable header row: treat it as data, columns = email, first name, last name.
    if (head.some((c) => c.includes("@"))) { data = records; ei = 0; fi = 1; li = 2; }
    else {
      out.problems.push({ line: records[0].line, email: "", reason: 'No "email" column found. Use the template: email, first_name, last_name.' });
      return out;
    }
  }
  const seen = new Set();
  for (const { line, cells } of data) {
    const email = (cells[ei] || "").trim();
    const first = fi >= 0 ? (cells[fi] || "").trim() : "";
    const last = li >= 0 ? (cells[li] || "").trim() : "";
    if (!email) { out.problems.push({ line, email: "", reason: "No email address" }); continue; }
    if (!EMAIL_RE.test(email)) { out.problems.push({ line, email, reason: "Not a valid email address" }); continue; }
    const key = email.toLowerCase();
    if (seen.has(key)) { out.problems.push({ line, email, reason: "Duplicate in this file" }); continue; }
    seen.add(key);
    if (!first) out.noName += 1;
    out.rows.push({ email, first_name: first, name: `${first} ${last}`.trim() || first, kind: "manual" });
  }
  return out;
}

export const TEMPLATE_CSV =
  "email,first_name,last_name\r\njane.citizen@example.com,Jane,Citizen\r\nalex.smith@example.com,Alex,Smith\r\n";

export function downloadCsvTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "bulk-email-recipients-template.csv";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
